import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAccount } from 'wagmi';
import { useAppKit } from '@reown/appkit/react';
import { getAllGames } from '../../data/games';
import { getAutoDrawSettings, saveAutoDrawSettings } from '../../api/playerApi';

function AutoDrawModal({ isOpen, onClose }) {
  const { address } = useAccount();
  const { open } = useAppKit();
  const games = getAllGames();

  const [settings, setSettings] = useState({
    enabled: false,
    gameId: 'strike',
    numberCount: 1,
    frequency: 'every_draw',
    maxEntries: 10,
    stopOnWin: false,
    minBalance: 0,
  });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (isOpen && address) {
      const load = async () => {
        const s = await getAutoDrawSettings(address);
        if (s) setSettings(s);
      };
      load();
      setSaved(false);
    }
  }, [isOpen, address]);

  if (!isOpen) return null;

  const selectedGame = games.find((g) => g.id === settings.gameId);
  const maxAllowed = selectedGame?.maxNumbersPerTicket || 10;

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    if (!address) return;

    if (settings.numberCount > maxAllowed) {
      toast.error(`${selectedGame?.name || 'This game'} allows a maximum of ${maxAllowed} numbers per ticket.`);
      return;
    }

    setLoading(true);
    await saveAutoDrawSettings(address, settings);
    setLoading(false);
    toast.success('AutoDraw settings saved! Visit the History page to review them.');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-dark-secondary border border-hash-gold/20 rounded-2xl p-6 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <img src="/images/icon-autodraw.svg" alt="" className="w-5 h-5" />
            <h3 className="text-xl font-bold text-white">Configure AutoDraw</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {!address ? (
          /* Not Connected */
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto rounded-full bg-hash-gold/10 border border-hash-gold/20 flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-hash-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <p className="text-gray-400 mb-6">Connect your wallet to configure AutoDraw settings.</p>
            <button
              onClick={() => open()}
              className="btn-gold px-8 py-3 text-sm"
            >
              Connect Wallet
            </button>
          </div>
        ) : (
          /* Configuration Form */
          <div className="space-y-4">
            {/* Enable Toggle */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-dark-surface/50 border border-white/5">
              <span className="text-white font-medium text-sm">Enable AutoDraw</span>
              <button
                onClick={() => handleChange('enabled', !settings.enabled)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  settings.enabled ? 'bg-hash-gold' : 'bg-gray-600'
                }`}
              >
                <div
                  className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                    settings.enabled ? 'translate-x-6' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            {/* Game Selection */}
            <div>
              <label className="block text-gray-400 text-xs mb-1.5">Game</label>
              <select
                value={settings.gameId}
                onChange={(e) => handleChange('gameId', e.target.value)}
                className="w-full dark-input text-sm"
              >
                {games.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Numbers per Entry */}
            <div>
              <label className="block text-gray-400 text-xs mb-1.5">Numbers per Entry</label>
              <input
                type="number"
                min={1}
                max={10}
                value={settings.numberCount}
                onChange={(e) => handleChange('numberCount', parseInt(e.target.value, 10) || 1)}
                className="w-full dark-input text-sm"
              />
            </div>

            {/* Frequency */}
            <div>
              <label className="block text-gray-400 text-xs mb-1.5">Frequency</label>
              <select
                value={settings.frequency}
                onChange={(e) => handleChange('frequency', e.target.value)}
                className="w-full dark-input text-sm"
              >
                <option value="every_draw">Every Draw</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>

            {/* Max Entries */}
            <div>
              <label className="block text-gray-400 text-xs mb-1.5">Max Entries</label>
              <input
                type="number"
                min={1}
                max={100}
                value={settings.maxEntries}
                onChange={(e) => handleChange('maxEntries', parseInt(e.target.value, 10) || 1)}
                className="w-full dark-input text-sm"
              />
            </div>

            {/* Stop on Win */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="stopOnWin"
                checked={settings.stopOnWin}
                onChange={(e) => handleChange('stopOnWin', e.target.checked)}
                className="w-4 h-4 rounded border-hash-gold/30 bg-dark-surface text-hash-gold focus:ring-hash-gold"
              />
              <label htmlFor="stopOnWin" className="text-gray-300 text-sm">
                Stop after a win
              </label>
            </div>

            {/* Min Balance */}
            <div>
              <label className="block text-gray-400 text-xs mb-1.5">Min Balance (USDT)</label>
              <input
                type="number"
                min={0}
                value={settings.minBalance}
                onChange={(e) => handleChange('minBalance', parseFloat(e.target.value) || 0)}
                className="w-full dark-input text-sm"
              />
            </div>

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={loading}
              className="w-full btn-gold py-3 text-sm flex items-center justify-center gap-2"
            >
              {loading ? (
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : saved ? (
                <>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Saved!
                </>
              ) : (
                'Save Configuration'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AutoDrawModal;
